import type { PoiFeatureProperties } from '@/components/map/utils/poiFeatureProperties';
import type { VanFeatureProperties } from '@/components/map/utils/vanFeatureProperties';
import type { MapSelection } from '@/hooks/useSelectionStats';

/**
 * Where a highlight-only (located) feature came from. Deliberately NOT carried on
 * `SelectedFeature`: a selection's provenance is unstable — a table- or search-originated
 * POI can be re-selected through Previous/Next navigation, which would silently overwrite
 * any source tag — so no reset rule is allowed to depend on how a selection was made.
 * `located` origin IS stable by construction: only a locate command ever writes it.
 */
export type LocateOrigin = 'table' | 'search';

/**
 * The feature whose UI is open: a POI (details panel) or an AOI/DMA (stats card).
 * The aoi/dma variants are exactly `MapSelection`, so the stats-card machinery
 * (`useSelectionStats`, `MapSelectionStatsCard`) can consume them unchanged.
 */
export type SelectedFeature =
    | { type: 'poi'; poi: PoiFeatureProperties; coords: [number, number] | null }
    // A Find & Fix leak entity. Carries the same feature-properties shape (the leak
    // feature's own properties are its details representation); the discriminant decides
    // which details panel opens. The leak's `leak:`-prefixed id drives the shared
    // selected-marker highlight and is never parsed.
    | { type: 'leak'; leak: PoiFeatureProperties; coords: [number, number] | null }
    | { type: 'van'; van: VanFeatureProperties; coords: [number, number] | null }
    | MapSelection;

/** A highlight-only feature (table Locate, DMA search). Never opens any panel or card. */
export type LocatedFeature =
    // `id` is the map feature id — a raw GUID for POI entities, `leak:`-prefixed for leak
    // entities (the table's Locate targets whichever its row's map_entity_id names).
    { type: 'poi'; id: string } | { type: 'aoi'; name: string } | { type: 'dma'; ident: string };

export interface LocatedState {
    feature: LocatedFeature;
    origin: LocateOrigin;
}

/**
 * A mutually exclusive map tool mode. While a tool is active, MapView suppresses
 * all feature interaction (POI/AOI/DMA/valve clicks and hovers, click dismissal)
 * so the tool owns every map click. Only the radial measuring tool exists today;
 * the union is where future tools (e.g. other measure/draw modes) slot in.
 */
export type MapToolId = 'radial';

/**
 * The whole interaction-state slice. Invariants maintained by the reducer:
 * `selection` and `located` are never both non-null — selecting clears any locate,
 * locating clears any selection — so the map can never show a card/details panel
 * that disagrees with the painted highlight. `activeTool` is likewise never
 * non-null alongside either: activating a tool closes any open card/details/
 * highlight, and selecting or locating a feature deactivates the tool.
 */
export interface MapInteractionState {
    selection: SelectedFeature | null;
    located: LocatedState | null;
    activeTool: MapToolId | null;
}

export const INITIAL_MAP_INTERACTION_STATE: MapInteractionState = Object.freeze({
    selection: null,
    located: null,
    activeTool: null,
});
